import { tr } from '@/lib/i18n-core';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';
import { LineTaxBadge, LineTaxDialog } from './LineTax';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Package, Printer, Receipt, Trash2 } from 'lucide-react';
import { groupPackageLines, type LineCoverageState } from '@his/shared';
import { del, get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { dateTime, humanize, money, newIdempotencyKey, toIsoDate } from '@/lib/utils';
import { printDocument } from '@/lib/print';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { DataTable, Dialog, PageHeader } from '@/components/ui/layout';
import { LinkChips, type LinkChip } from '@/components/LinkChips';
import { Alert, Button, Card, CardContent, CardHeader, CardTitle, Field, PageSkeleton, Input, StatusBadge, Textarea, Tooltip } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import type { CreditableItem } from './CreditNotePage';
import { RequestRefundDialog, type RefundablePayment } from './RequestRefundDialog';
import { usePrintReceipt } from './receipt-print';
import { InvoiceInsurancePanel } from './InvoiceInsurancePanel';
import { BillPackagesCard, type BillPackage } from './BillPackagesCard';
import { PackageLineBadge } from './PackageLineBadge';
import { SourceStateBadge } from '../opd/SourceStateBadge';
import { useAuthorisationValue } from '../opd/AuthorisationContext';
import { invoiceLineKeys } from '../insurance/authorisation';
import { ClaimAction, CoverageCell, FollowUpPanel, LockedLinesNote } from '../insurance/flow/BillCoverage';
import { EligibilityPanel } from '../insurance/flow/EligibilityPanel';
import { VisitInsuranceCard } from '../insurance/flow/VisitInsurance';
import { isApprovalLocked } from '../insurance/flow/coverage-badge';

interface InvoiceDetail {
  id: string;
  number: string;
  estimateNumber?: string | null;
  context: string;
  status: string;
  createdAt: string;
  issuedAt: string | null;
  notes: string | null;
  discountReason: string | null;
  cancelReason: string | null;
  encounterId: string | null;
  appointmentId: string | null;
  appointment?: { id: string; slotAt: string } | null;
  admissionId: string | null;
  netMinor: number;
  taxMinor: number;
  creditMinor: number;
  panelCapNote?: string | null;
  paidMinor: number;
  patient: { id: string; mrn: string; fullName: string; category: string; phone: string | null } | null;
  walkInName: string | null;
  payer: { id: string; name: string; coPayPct: number } | null;
  branch: { id: string; name: string };
  totals: { grossMinor: number; discountMinor: number; taxMinor: number; netMinor: number; paidMinor: number; creditMinor: number; balanceMinor: number };
  everClaimed: boolean;
  insuranceTotalMinor: number;
  selfPayTotalMinor: number;
  creditNotedMinor: number;
  customerCreditMinor: number;
  items: (CreditableItem & {
    unitPriceMinor: number;
    discountMinor: number;
    taxCategory?: string | null;
    taxReason?: string | null;
    patientShareMinor?: number | null;
    panelShareMinor?: number | null;
    orderSourceKey?: string | null;
    coverageState?: LineCoverageState;
    lockedByApproval?: boolean;
    approvedPct?: number | null;
    packageId?: string | null;
    packageInstanceId?: string | null;
    package?: { id: string; code: string; name: string } | null;
    patientPackageId?: string | null;
    packageRole?: string | null;
    packageValueMinor?: number | null;
    productId?: string | null;
    stockBatchId?: string | null;
  })[];
  patientPackages?: BillPackage[];
  payments: {
    id: string;
    amountMinor: number;
    mode: string;
    status: string;
    txnRef: string | null;
    receivedAt: string;
    receiptNo: string | null;
    refundedMinor: number;
    pendingRefundMinor: number;
    refundableMinor: number;
  }[];
  refunds: {
    id: string;
    paymentId: string;
    amountMinor: number;
    status: string;
    reason: string;
    receiptNo: string | null;
    requestedAt: string;
    paidAt: string | null;
  }[];
  creditNotes: {
    id: string;
    number: string;
    status: string;
    reason: string;
    settlement: string;
    netMinor: number;
    requestedAt: string;
    appliedAt: string | null;
  }[];
}

const CREDITABLE_STATUS = ['issued', 'partially_paid', 'paid'];

function statementLink(invoice: InvoiceDetail): string | null {
  if (invoice.admissionId) return `/billing/statement?admissionId=${invoice.admissionId}`;
  if (invoice.encounterId) return `/billing/statement?encounterId=${invoice.encounterId}`;
  if (invoice.patient) return `/billing/statement?patientId=${invoice.patient.id}`;
  return null;
}

function relatedLinks(invoice: InvoiceDetail, can: (...permissions: string[]) => boolean): LinkChip[] {
  const links: LinkChip[] = [];
  if (invoice.encounterId && can('encounter.read', 'encounter.read.own', 'encounter.write')) {
    links.push({ to: `/consultations/${invoice.encounterId}`, label: tr('billing', 'consultation') });
  }
  if (invoice.appointment && can('appointment.read', 'appointment.manage')) {
    const date = toIsoDate(new Date(invoice.appointment.slotAt));
    links.push({ to: `/appointments${qs({ view: 'list', date, q: invoice.patient?.mrn })}`, label: tr('billing', 'appointment') });
  }
  return links;
}

/** One invoice: what was charged, what was paid, and what is still owed (SRS 9.2). */
export function InvoiceDetailPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [collecting, setCollecting] = useState(false);
  const [params, setParams] = useSearchParams();
  const payReturnTo = params.get('returnTo') ? safeReturnTo(params.get('returnTo'), '/appointments') : null;
  const [refunding, setRefunding] = useState<RefundablePayment | null>(null);
  const [removing, setRemoving] = useState<{ id: string; description: string; instanceId?: string; usage?: boolean } | null>(null);
  const [taxing, setTaxing] = useState<{ id: string; description: string; taxCategory?: string | null } | null>(null);
  const printReceipt = usePrintReceipt();

  const invoice = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => get<InvoiceDetail>(`/invoices/${invoiceId}`),
    enabled: Boolean(invoiceId),
  });
  const authorisation = useAuthorisationValue({ invoiceId: invoiceId ?? '' }, Boolean(invoice.data?.payer));

  const print = useMutation({
    mutationFn: (size: 'A4' | 'thermal') => get<{ html: string }>(`/invoices/${invoiceId}/print?size=${size}`),
    onSuccess: (doc) => printDocument(doc.html),
    onError: (error) => toast.error(tr('billing', 'could_not_print'), describeError(error)),
  });

  const printCreditNote = useMutation({
    mutationFn: (id: string) => get<{ html: string }>(`/credit-notes/${id}/print`),
    onSuccess: (doc) => printDocument(doc.html),
    onError: (error) => toast.error(tr('billing', 'could_not_print'), describeError(error)),
  });

  const issue = useMutation({
    mutationFn: () => post(`/invoices/${invoiceId}/issue`),
    onSuccess: () => {
      toast.success(tr('billing', 'invoice_issued'));
      refresh();
    },
    onError: (error) => toast.error(tr('billing', 'could_not_issue_the_invoice'), describeError(error)),
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] });
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    void queryClient.invalidateQueries({ queryKey: ['refunds'] });
  }

  const autoCollect = params.get('collect') === '1';
  const collectFrom = invoice.data;
  useEffect(() => {
    if (!autoCollect || !collectFrom) return;
    const owing = collectFrom.totals.balanceMinor > 0 && !['cancelled', 'refunded', 'draft'].includes(collectFrom.status);
    if (owing && can('payment.collect')) setCollecting(true);
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('collect');
        return next;
      },
      { replace: true },
    );
  }, [autoCollect, collectFrom, can, setParams]);

  if (invoice.isLoading) {
    return (
      <PageSkeleton />
    );
  }
  if (!invoice.data) return <Alert variant="destructive">{tr('billing', 'that_invoice_could_not_be_opened')}</Alert>;

  const i = invoice.data;
  const outstanding = i.totals.balanceMinor > 0 && !['cancelled', 'refunded', 'draft'].includes(i.status);
  const statement = statementLink(i);
  const canCreditNote = can('invoice.credit_note') && CREDITABLE_STATUS.includes(i.status) && !i.everClaimed;
  const groupedItems = groupPackageLines(invoice.data?.items ?? [], (item) => item.id);
  const orderedItems = groupedItems.flatMap((group) => (group.kind === 'line' ? [group.item] : group.items));
  const packageHeads = new Map(groupedItems.flatMap((group) => (group.kind === 'package' ? [[group.items[0].id, group] as const] : [])));
  const canRemoveLines =
    can('invoice.line.remove') && ['draft', 'issued'].includes(i.status) && i.paidMinor === 0 && i.payments.length === 0 && !i.everClaimed;
  const canRemoveUsage = can('invoice.line.remove') && !['draft', 'cancelled', 'refunded'].includes(i.status) && !i.everClaimed;
  const removableUsage = (row: InvoiceDetail['items'][number]) => row.packageRole === 'usage' && !row.productId && !row.stockBatchId;
  const canOverrideTax =
    can('invoice.tax_override') && ['draft', 'issued'].includes(i.status) && i.paidMinor === 0 && i.payments.length === 0 && !i.everClaimed;
  const canRefund = can('payment.refund', 'payment.refund.approve');
  const refundsByPayment = new Map<string, InvoiceDetail['refunds']>();
  for (const refund of i.refunds) {
    refundsByPayment.set(refund.paymentId, [...(refundsByPayment.get(refund.paymentId) ?? []), refund]);
  }
  const panelShare = i.totals.creditMinor;
  const billPackages = i.patientPackages ?? [];
  const billHasPackage = billPackages.length > 0;

  return (
    <>
      <PageHeader
        title={`${i.status === 'draft' ? 'Estimate' : 'Invoice'} ${i.number}`}
        description={`${humanize(i.context)} · ${i.branch.name} · ${dateTime(i.issuedAt ?? i.createdAt)}${i.estimateNumber && i.status !== 'draft' ? ` · issued from estimate ${i.estimateNumber}` : ''}`}
        actions={
          <>
            <Button variant="outline" onClick={() => print.mutate('thermal')} loading={print.isPending}>
              <Receipt className="h-4 w-4" /> {tr('billing', 'receipt')}
            </Button>
            <Button variant="outline" onClick={() => print.mutate('A4')} loading={print.isPending}>
              <Printer className="h-4 w-4" /> {tr('billing', 'a4_bill')}
            </Button>
            {statement && (
              <Link to={statement}>
                <Button variant="outline">
                  <FileText className="h-4 w-4" /> {tr('billing', 'statement')}
                </Button>
              </Link>
            )}
            {canCreditNote && (
              <Button variant="outline" onClick={() => navigate(`/billing/invoices/${i.id}/credit-note`)}>
                {tr('billing', 'credit_note')}
              </Button>
            )}
            {i.status === 'draft' && can('invoice.create') && (
              <Button onClick={() => issue.mutate()} loading={issue.isPending}>
                {tr('billing', 'issue_invoice')}
              </Button>
            )}
            {outstanding && can('payment.collect') && <Button onClick={() => setCollecting(true)}>{tr('billing', 'collect_payment')}</Button>}
          </>
        }
      />

      <LinkChips links={relatedLinks(i, can)} className="mb-4" />

      {i.status === 'draft' && (
        <Alert variant="warning" className="mb-4">
          {tr('billing', 'this_is_an_estimate_nothing_can_be_paid_against')}
        </Alert>
      )}
      {i.status === 'cancelled' && (
        <Alert variant="destructive" className="mb-4">
          {tr('billing', 'cancelled')}{i.cancelReason ? `: ${i.cancelReason}` : ''}{tr('billing', 'the_bill_is_kept_for_the_record_and_nothing_is')}
        </Alert>
      )}
      {i.panelCapNote && (
        <Alert variant="warning" className="mb-4" title={tr('billing', 'the_insurers_share_was_limited')}>
          {i.panelCapNote}
        </Alert>
      )}
      {i.everClaimed && (
        <Alert className="mb-4">{tr('billing', 'this_bill_was_sent_to_a_payer_on_a_claim_so_it')}</Alert>
      )}

      <LockedLinesNote count={i.items.filter(isApprovalLocked).length} />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {billHasPackage && <BillPackagesCard packages={billPackages} />}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{tr('billing', 'charges')}</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                dense
                rows={orderedItems}
                rowKey={(row) => row.id}
                columns={[
                  {
                    header: tr('billing', 'description'),
                    cell: (row) => (
                      <div className={row.packageInstanceId ? 'border-s-2 border-primary/30 ps-3' : undefined}>
                        {packageHeads.get(row.id) && (
                          <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                            <Package className="h-3.5 w-3.5" aria-hidden /> {packageHeads.get(row.id)?.name}
                          </p>
                        )}
                        <p className="flex flex-wrap items-center gap-2">
                          {row.description}
                          <PackageLineBadge role={row.packageRole} billHasPackage={billHasPackage} />
                        </p>
                        {row.packageRole === 'usage' && row.packageValueMinor ? (
                          <p className="text-xs text-muted-foreground">{tr('billing', 'list_value_x', { amount: money(row.packageValueMinor) })}</p>
                        ) : null}
                        {row.creditedQty > 0 && (
                          <p className="text-xs text-warning">
                            {tr('billing', 'credited')} {row.creditedQty} · {money(row.creditedNetMinor)}
                          </p>
                        )}
                        {row.returnedQty > 0 && <p className="text-xs text-warning">{tr('billing', 'returned')} {row.returnedQty}</p>}
                        <LineTaxBadge taxCategory={row.taxCategory} taxReason={row.taxReason} />
                      </div>
                    ),
                  },
                  { header: tr('billing', 'qty'), align: 'right', cell: (row) => row.qty },
                  { header: tr('billing', 'rate_2'), align: 'right', cell: (row) => money(row.unitPriceMinor) },
                  {
                    header: tr('billing', 'discount'),
                    align: 'right',
                    cell: (row) => (row.discountMinor ? money(row.discountMinor) : '—'),
                  },
                  { header: tr('billing', 'tax'), align: 'right', cell: (row) => (row.taxMinor ? money(row.taxMinor) : '—') },
                  { header: tr('billing', 'amount'), align: 'right', cell: (row) => money(row.netMinor) },
                  ...(i.payer
                    ? [
                        {
                          header: tr('billing', 'patient'),
                          align: 'right' as const,
                          cell: (row: InvoiceDetail['items'][number]) => money(row.patientShareMinor ?? row.netMinor - (row.panelShareMinor ?? 0)),
                        },
                        {
                          header: tr('billing', 'panel'),
                          align: 'right' as const,
                          cell: (row: InvoiceDetail['items'][number]) => (row.panelShareMinor ? money(row.panelShareMinor) : '—'),
                        },
                        {
                          header: tr('billing', 'authorisation'),
                          cell: (row: InvoiceDetail['items'][number]) => <SourceStateBadge line={authorisation.lineFor(invoiceLineKeys(row))} />,
                        },
                        {
                          header: tr('billing', 'nx_coverage'),
                          cell: (row: InvoiceDetail['items'][number]) => <CoverageCell item={row} />,
                        },
                      ]
                    : []),
                  ...(canOverrideTax
                    ? [
                        {
                          header: '',
                          align: 'right' as const,
                          cell: (row: InvoiceDetail['items'][number]) => (
                            <Button size="sm" variant="ghost" onClick={() => setTaxing({ id: row.id, description: row.description, taxCategory: row.taxCategory })}>
                              {tr('billing', 'vat')}
                            </Button>
                          ),
                        },
                      ]
                    : []),
                  ...(canRemoveLines || (canRemoveUsage && i.items.some(removableUsage))
                    ? [
                        {
                          header: '',
                          align: 'right' as const,
                          cell: (row: InvoiceDetail['items'][number]) => {
                            const head = packageHeads.get(row.id);
                            if (row.packageInstanceId && !head) return null;
                            if (row.packageRole === 'sale') return null;
                            if (row.packageRole === 'usage' && !removableUsage(row)) return null;
                            if (!canRemoveLines && !removableUsage(row)) return null;
                            const members = head ? head.items : [row];
                            const approvedLock = members.some(isApprovalLocked);
                            const lockReason = approvedLock ? ('nx_locked_by_approval' as const) : (members.map((member) => authorisation.lockReasonKey(invoiceLineKeys(member))).find(Boolean) ?? null);
                            const button = (
                              <Button
                                size="icon"
                                variant="ghost"
                                disabled={Boolean(lockReason)}
                                aria-label={head ? tr('billing', 'remove_package') : tr('billing', 'delete_x', { description: row.description })}
                                onClick={() =>
                                  setRemoving(
                                    head
                                      ? { id: row.id, description: head.name, instanceId: head.instanceId }
                                      : { id: row.id, description: row.description, usage: removableUsage(row) },
                                  )
                                }
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            );
                            return lockReason ? (
                              <Tooltip content={tr('billing', lockReason)}>
                                <span className="inline-flex">{button}</span>
                              </Tooltip>
                            ) : (
                              button
                            );
                          },
                        },
                      ]
                    : []),
                ]}
              />
            </CardContent>
          </Card>

          {i.payer && i.patient && (
            <FollowUpPanel
              items={i.items}
              lineOf={(item) => authorisation.lineFor(invoiceLineKeys(i.items.find((row) => row.id === item.id) ?? { id: item.id }))}
              scope={{ patientId: i.patient.id, payerId: i.payer.id, encounterId: i.encounterId, admissionId: i.admissionId }}
              channel={authorisation.summary?.payers.find((payer) => payer.id === i.payer?.id)?.channel ?? null}
            />
          )}
          {i.payer && <VisitInsuranceCard encounterId={i.encounterId} admissionId={i.admissionId} />}

          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{tr('billing', 'payments')}</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                dense
                rows={i.payments}
                rowKey={(row) => row.id}
                empty={tr('billing', 'no_payments_recorded_yet')}
                columns={[
                  { header: tr('billing', 'received_2'), cell: (row) => dateTime(row.receivedAt) },
                  { header: tr('billing', 'receipt'), cell: (row) => <span className="tabular text-xs">{row.receiptNo ?? '—'}</span> },
                  { header: tr('billing', 'mode'), cell: (row) => humanize(row.mode) },
                  { header: tr('billing', 'reference'), cell: (row) => row.txnRef ?? '—' },
                  { header: tr('billing', 'status'), cell: (row) => <StatusBadge status={row.status} /> },
                  {
                    header: tr('billing', 'amount'),
                    align: 'right',
                    cell: (row) => (
                      <div>
                        <p>{money(row.amountMinor)}</p>
                        {row.refundedMinor > 0 && <p className="text-xs text-destructive">− {money(row.refundedMinor)} {tr('billing', 'refunded')}</p>}
                        {row.pendingRefundMinor > 0 && <p className="text-xs text-warning">{money(row.pendingRefundMinor)} {tr('billing', 'refund_waiting')}</p>}
                      </div>
                    ),
                  },
                  {
                    header: '',
                    align: 'right',
                    cell: (row) => (
                      <div className="flex justify-end gap-1">
                        {row.status === 'completed' && row.mode !== 'deposit' && (
                          <>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={tr('billing', 'print_receipt')}
                              onClick={() => printReceipt.mutate({ kind: 'payment', id: row.id, size: 'thermal' })}
                            >
                              <Receipt className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              aria-label={tr('billing', 'print_receipt_on_a4')}
                              onClick={() => printReceipt.mutate({ kind: 'payment', id: row.id, size: 'A4' })}
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {canRefund && row.refundableMinor > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              setRefunding({
                                id: row.id,
                                mode: row.mode,
                                amountMinor: row.amountMinor,
                                refundableMinor: row.refundableMinor,
                                receiptNo: row.receiptNo,
                              })
                            }
                          >
                            {tr('billing', 'refund')}
                          </Button>
                        )}
                      </div>
                    ),
                  },
                ]}
              />
            </CardContent>
          </Card>

          {i.refunds.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{tr('billing', 'refunds')}</CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  dense
                  rows={i.refunds}
                  rowKey={(row) => row.id}
                  columns={[
                    { header: tr('billing', 'requested'), cell: (row) => dateTime(row.requestedAt) },
                    { header: tr('billing', 'voucher'), cell: (row) => <span className="tabular text-xs">{row.receiptNo ?? '—'}</span> },
                    { header: tr('billing', 'reason'), cell: (row) => <span className="text-xs">{row.reason}</span> },
                    { header: tr('billing', 'status'), cell: (row) => <StatusBadge status={row.status} /> },
                    { header: tr('billing', 'amount'), align: 'right', cell: (row) => <span className="text-destructive">− {money(row.amountMinor)}</span> },
                    {
                      header: '',
                      align: 'right',
                      cell: (row) =>
                        row.status === 'paid' ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={tr('billing', 'print_refund_voucher')}
                            onClick={() => printReceipt.mutate({ kind: 'refund', id: row.id, size: 'thermal' })}
                          >
                            <Receipt className="h-4 w-4" />
                          </Button>
                        ) : null,
                    },
                  ]}
                />
              </CardContent>
            </Card>
          )}

          {i.creditNotes.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">{tr('billing', 'credit_notes')}</CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  dense
                  rows={i.creditNotes}
                  rowKey={(row) => row.id}
                  columns={[
                    { header: tr('billing', 'number'), cell: (row) => <span className="tabular font-medium">{row.number}</span> },
                    { header: tr('billing', 'date'), cell: (row) => dateTime(row.appliedAt ?? row.requestedAt) },
                    { header: tr('billing', 'reason'), cell: (row) => <span className="text-xs">{row.reason}</span> },
                    { header: tr('billing', 'status'), cell: (row) => <StatusBadge status={row.status} /> },
                    { header: tr('billing', 'credit'), align: 'right', cell: (row) => <span className="text-destructive">− {money(row.netMinor)}</span> },
                    {
                      header: '',
                      align: 'right',
                      cell: (row) => (
                        <Button size="icon" variant="ghost" aria-label={tr('billing', 'print_credit_note')} onClick={() => printCreditNote.mutate(row.id)}>
                          <Printer className="h-4 w-4" />
                        </Button>
                      ),
                    },
                  ]}
                />
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">{tr('billing', 'summary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5 text-sm">
              <Row label={tr('billing', 'status')} value={<StatusBadge status={i.status} />} />
              <Row
                label={tr('billing', 'patient')}
                value={
                  i.patient ? (
                    <Link className="underline" to={`/patients/${i.patient.id}`}>
                      {i.patient.fullName}
                    </Link>
                  ) : (
                    i.walkInName ?? 'Walk-in'
                  )
                }
              />
              {i.patient && <Row label={tr('billing', 'mrn')} value={i.patient.mrn} />}
              <Row label={tr('billing', 'payer')} value={i.payer?.name ?? 'Self-pay'} />
              {i.payer && <Row label={tr('billing', 'nx_insurance_total')} value={money(i.insuranceTotalMinor)} />}
              {i.payer && <Row label={tr('billing', 'nx_self_pay_total')} value={money(i.selfPayTotalMinor)} />}
              <div className="my-2 h-px bg-border" />
              <Row label={tr('billing', 'gross')} value={money(i.totals.grossMinor)} />
              <Row label={tr('billing', 'discount')} value={money(i.totals.discountMinor)} />
              <Row label={tr('billing', 'tax')} value={money(i.totals.taxMinor)} />
              {i.creditNotedMinor > 0 && <Row label={tr('billing', 'credit_notes')} value={`− ${money(i.creditNotedMinor)}`} />}
              <Row label={tr('billing', 'net')} value={<strong>{money(i.totals.netMinor)}</strong>} />
              {panelShare > 0 && <Row label={tr('billing', 'panel_share')} value={money(panelShare)} />}
              {panelShare > 0 && <Row label={tr('billing', 'patient_share')} value={money(i.totals.netMinor - panelShare)} />}
              <Row label={tr('billing', 'paid')} value={money(i.totals.paidMinor)} />
              <div className="my-2 h-px bg-border" />
              <Row
                label={tr('billing', 'balance')}
                value={
                  <strong className={i.totals.balanceMinor > 0 ? 'text-destructive' : 'text-success'}>
                    {money(i.totals.balanceMinor)}
                  </strong>
                }
              />
              {i.customerCreditMinor > 0 && <Row label={tr('billing', 'credit_held_for_customer')} value={money(i.customerCreditMinor)} />}
              {i.discountReason && (
                <p className="pt-2 text-xs text-muted-foreground">{tr('billing', 'discount_reason')} {i.discountReason}</p>
              )}
            </CardContent>
          </Card>
          <InvoiceInsurancePanel invoice={i} summary={authorisation.summary} />
          {i.payer && i.patient && (
            <EligibilityPanel
              scope={{ patientId: i.patient.id, payerId: i.payer.id, payerName: i.payer.name, encounterId: i.encounterId, admissionId: i.admissionId }}
              canSend={authorisation.summary?.canSend ?? true}
            />
          )}
          {i.payer && i.patient && (
            <ClaimAction
              invoice={{
                id: i.id,
                number: i.number,
                status: i.status,
                netMinor: i.totals.netMinor,
                creditMinor: i.totals.creditMinor,
                paidMinor: i.totals.paidMinor,
                everClaimed: i.everClaimed,
                insuranceTotalMinor: i.insuranceTotalMinor,
                encounterId: i.encounterId,
                admissionId: i.admissionId,
                patientId: i.patient.id,
                payerId: i.payer.id,
              }}
            />
          )}
        </div>
      </div>

      {collecting && (
        <CollectPaymentDialog
          open={collecting}
          invoice={i}
          onClose={() => setCollecting(false)}
          onCollected={() => {
            setCollecting(false);
            refresh();
            if (payReturnTo) navigate(payReturnTo);
          }}
        />
      )}
      {refunding && (
        <RequestRefundDialog
          payment={refunding}
          invoiceNumber={i.number}
          overpaidMinor={Math.max(
            0,
            i.totals.paidMinor - (i.totals.netMinor - i.totals.creditMinor) - i.payments.reduce((sum, row) => sum + row.pendingRefundMinor, 0),
          )}
          creditNoteHref={canCreditNote ? `/billing/invoices/${i.id}/credit-note` : null}
          onClose={() => setRefunding(null)}
          onDone={() => {
            setRefunding(null);
            refresh();
          }}
        />
      )}
      {taxing && (
        <LineTaxDialog
          invoiceId={i.id}
          line={taxing}
          onClose={() => setTaxing(null)}
          onDone={() => {
            setTaxing(null);
            refresh();
          }}
        />
      )}
      {removing?.usage && (
        <RemoveUsageLineDialog
          invoiceId={i.id}
          line={removing}
          onClose={() => setRemoving(null)}
          onRemoved={() => {
            setRemoving(null);
            refresh();
          }}
        />
      )}
      {removing && !removing.usage && (
        <RemoveLineDialog
          invoiceId={i.id}
          line={removing}
          onClose={() => setRemoving(null)}
          onRemoved={() => {
            setRemoving(null);
            refresh();
          }}
        />
      )}
    </>
  );
}

function RemoveUsageLineDialog({
  invoiceId,
  line,
  onClose,
  onRemoved,
}: {
  invoiceId: string;
  line: { id: string; description: string };
  onClose: () => void;
  onRemoved: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const remove = useMutation({
    mutationFn: () => del(`/invoices/${invoiceId}/lines/${line.id}`, { body: { reason: reason.trim() } }),
    onSuccess: () => {
      toast.success(tr('billing', 'line_deleted'));
      onRemoved();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <ConfirmDialog
      destructive
      title={tr('billing', 'take_a_package_line_off_the_bill')}
      description={line.description}
      confirmLabel={tr('billing', 'delete_line')}
      loading={remove.isPending}
      disabled={reason.trim().length < 5}
      onClose={onClose}
      onConfirm={() => {
        setError(null);
        return remove.mutateAsync();
      }}
    >
      <div className="space-y-4">
        <Alert>{tr('billing', 'a_package_line_carries_no_money_removing_it_gives')}</Alert>
        <Field label={tr('billing', 'reason')} required hint={tr('billing', 'at_least_five_characters')}>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={400} autoFocus />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </ConfirmDialog>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular text-end">{value}</span>
    </div>
  );
}

function RemoveLineDialog({
  invoiceId,
  line,
  onClose,
  onRemoved,
}: {
  invoiceId: string;
  line: { id: string; description: string; instanceId?: string };
  onClose: () => void;
  onRemoved: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: () =>
      line.instanceId
        ? del(`/invoices/${invoiceId}/packages/${line.instanceId}`, { body: { reason: reason.trim() } })
        : del(`/invoices/${invoiceId}/lines/${line.id}`, { body: { reason: reason.trim() } }),
    onSuccess: () => {
      toast.success(line.instanceId ? tr('billing', 'package_removed') : tr('billing', 'line_deleted'));
      onRemoved();
    },
    onError: (caught) => setError(describeError(caught)),
  });

  return (
    <Dialog
      open
      onClose={onClose}
      title={line.instanceId ? tr('billing', 'delete_the_whole_package') : tr('billing', 'delete_a_line_charged_in_error')}
      description={line.description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setError(null);
              remove.mutate();
            }}
            loading={remove.isPending}
            disabled={reason.trim().length < 5}
          >
            {line.instanceId ? tr('billing', 'remove_package') : tr('billing', 'delete_line')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Alert variant="warning">
          {line.instanceId ? tr('billing', 'a_package_is_removed_as_a_whole_every_service') : tr('billing', 'only_for_a_charge_that_should_never_have_been')}
        </Alert>
        <Field label={tr('billing', 'reason')} required hint={tr('billing', 'at_least_five_characters')}>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={400} autoFocus />
        </Field>
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}

function CollectPaymentDialog({
  open,
  invoice,
  onClose,
  onCollected,
}: {
  open: boolean;
  invoice: InvoiceDetail;
  onClose: () => void;
  onCollected: () => void;
}) {
  const toast = useToast();
  const printReceipt = usePrintReceipt();
  const [amount, setAmount] = useState(formatMoneyInput(invoice.totals.balanceMinor));
  const [mode, setMode] = useState('cash');
  const [tendered, setTendered] = useState('');
  const [txnRef, setTxnRef] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [recorded, setRecorded] = useState<{ id: string; receiptNo: string | null; change: number } | null>(null);
  // One key per dialog opening, so a double-click replays instead of charging twice.
  const [idempotencyKey, setIdempotencyKey] = useState(() => newIdempotencyKey());

  const [attempted, setAttempted] = useState(false);

  const amountMinor = minorFromInput(amount);
  const tenderedMinor = tendered.trim() ? minorFromInput(tendered) : undefined;
  const change = tenderedMinor !== undefined && Number.isFinite(tenderedMinor) ? tenderedMinor - amountMinor : 0;
  const amountProblem = !Number.isFinite(amountMinor)
    ? tr('billing', 'payment_amount_not_a_number')
    : amountMinor <= 0
      ? tr('billing', 'payment_amount_above_zero')
      : amountMinor > invoice.totals.balanceMinor
        ? tr('billing', 'payment_amount_above_balance_x', { balance: money(invoice.totals.balanceMinor) })
        : null;
  const tenderedProblem =
    mode !== 'cash' || tenderedMinor === undefined
      ? null
      : !Number.isFinite(tenderedMinor)
        ? tr('billing', 'tendered_not_a_number')
        : !amountProblem && tenderedMinor < amountMinor
          ? tr('billing', 'tendered_less_than_amount_x', { short: money(amountMinor - tenderedMinor) })
          : null;
  const showAmountProblem = attempted || amount.trim() !== '';

  const collect = useMutation({
    mutationFn: () =>
      post<{ id: string; receiptNo: string | null }>(
        '/payments',
        {
          invoiceId: invoice.id,
          amountMinor,
          mode,
          ...(tenderedMinor !== undefined && mode === 'cash' ? { tenderedMinor } : {}),
          ...(txnRef ? { txnRef } : {}),
        },
        { idempotencyKey },
      ),
    onSuccess: (payment) => {
      toast.success(tr('billing', 'payment_recorded'), change > 0 ? tr('billing', 'change_due_x_2', { change: money(change) }) : undefined);
      setIdempotencyKey(newIdempotencyKey());
      setRecorded({ id: payment.id, receiptNo: payment.receiptNo, change: mode === 'cash' && change > 0 ? change : 0 });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  if (recorded) {
    return (
      <Dialog
        open={open}
        onClose={onCollected}
        title={tr('billing', 'payment_recorded')}
        description={recorded.receiptNo ? tr('billing', 'receipt_x', { receiptNo: recorded.receiptNo }) : undefined}
        footer={
          <>
            <Button variant="outline" onClick={() => printReceipt.mutate({ kind: 'payment', id: recorded.id, size: 'A4', original: true })}>
              <Printer className="h-4 w-4" /> {tr('billing', 'a4_receipt')}
            </Button>
            <Button variant="outline" onClick={() => printReceipt.mutate({ kind: 'payment', id: recorded.id, size: 'thermal', original: true })}>
              <Receipt className="h-4 w-4" /> {tr('billing', 'thermal_receipt')}
            </Button>
            <Button onClick={onCollected}>{tr('billing', 'done')}</Button>
          </>
        }
      >
        <div className="space-y-3">
          <Alert variant="success">{money(amountMinor)} {tr('billing', 'received_by')} {humanize(mode)}.</Alert>
          {recorded.change > 0 && <Alert variant="warning">{tr('billing', 'change_to_return')} {money(recorded.change)}</Alert>}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={tr('billing', 'collect_payment')}
      description={tr('billing', 'balance_on_x_x', { number: invoice.number, balanceMinor: money(invoice.totals.balanceMinor) })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tr('billing', 'cancel')}
          </Button>
          <Button
            onClick={() => {
              setError(null);
              setAttempted(true);
              if (amountProblem || tenderedProblem) return;
              collect.mutate();
            }}
            loading={collect.isPending}
          >
            {tr('billing', 'record_payment')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={tr('billing', 'amount')} required error={showAmountProblem ? (amountProblem ?? undefined) : undefined}>
          <Input
            type="text"
            inputMode="decimal"
            dir="ltr"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="text-lg tabular"
            autoFocus
          />
        </Field>

        <Field label={tr('billing', 'mode')} required>
          <ChoiceButtons
            layout="chips"
            value={mode}
            onChange={setMode}
            options={['cash', 'card', 'bank_transfer', 'mobile_wallet', 'online_gateway'].map((m) => ({ value: m, label: humanize(m, 'payment_mode') }))}
          />
        </Field>

        {mode === 'cash' ? (
          <Field label={tr('billing', 'cash_tendered')} hint={tr('billing', 'optional_used_to_compute_change')} error={tenderedProblem ?? undefined}>
            <Input type="text" inputMode="decimal" dir="ltr" value={tendered} onChange={(e) => setTendered(e.target.value)} className="tabular" />
          </Field>
        ) : (
          <Field label={tr('billing', 'reference')} hint={tr('billing', 'the_card_slip_transfer_or_wallet_reference_most')}>
            <Input value={txnRef} onChange={(e) => setTxnRef(e.target.value)} placeholder={tr('billing', 'transaction_or_slip_number')} />
          </Field>
        )}

        {mode === 'cash' && tenderedMinor !== undefined && !tenderedProblem && !amountProblem && change >= 0 && (
          <Alert variant={change > 0 ? 'warning' : 'success'}>
            {change > 0 ? tr('billing', 'change_to_return_x', { change: money(change) }) : tr('billing', 'exact_amount_tendered')}
          </Alert>
        )}
        {error && <Alert variant="destructive">{error}</Alert>}
      </div>
    </Dialog>
  );
}
